
import { useNavigate } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import Footer from '../components/Footer';
import useAuthModal from '../hooks/useAuthModal';
import { isAuthenticated } from '../utils/auth';
import { pushDataLayer } from '../utils/dataLayer';

export default function StartScreen() {
  const navigate = useNavigate();
  const authModal = useAuthModal();

  const proceedAction = (action) => {
    if (action === 'profile') {
      navigate('/profile');
      return;
    }
    navigate('/search');
  };

  const handleProtectedAction = (action) => {
    if (isAuthenticated()) {
      proceedAction(action);
      return;
    }
    const redirectTo = action === 'profile' ? '/profile' : '/search';
    authModal.openAuth(action, redirectTo);
  };

  const handlePrimaryAction = () => {
    handleProtectedAction('search');
  };

  const handleMainSearchClick = () => {
    pushDataLayer({
      event: 'ui_interaction',
      action: 'click',
      element: 'search_button',
      location: 'main',
      data_source: 'front',
    });
    handlePrimaryAction();
  };

  const handleStartSearchingClick = () => {
    pushDataLayer({
      event: 'ui_interaction',
      action: 'click',
      element: 'start_searching_btn',
      location: 'footer',
      data_source: 'front',
    });
    handlePrimaryAction();
  };

  const mobileHowItems = [
    {
      title: 'Smart search',
      description: 'AI finds relevant jobs for you',
      icon: (
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" strokeLinecap="round" />
        </svg>
      ),
    },
    {
      title: 'AI interview',
      description: 'Practice with a virtual interviewer',
      icon: (
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="18" height="14" rx="3" />
          <path d="m8 21 4-3 4 3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
    },
    {
      title: 'Analytics',
      description: 'Track progress and improve',
      icon: (
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M5 20V11" strokeLinecap="round" />
          <path d="M12 20V6" strokeLinecap="round" />
          <path d="M19 20V14" strokeLinecap="round" />
        </svg>
      ),
    },
    {
      title: 'Personalization',
      description: 'Tailored to your goals',
      icon: (
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="3" />
          <path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2" strokeLinecap="round" />
        </svg>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-[#F6F7FB] text-[#1E293B]">
      <SiteHeader authModal={authModal} showGuestContinue />

      <main>
        <section className="md:hidden">
          <div className="bg-white px-5 pt-7 pb-6 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#F7F8FA] px-3 py-1.5 text-xs font-medium text-gray-600">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Over 10,000 IT jobs
            </div>
            <h1 className="mt-4 text-[30px] leading-[1.15] font-semibold">
              The AI interview coach that trains you like
              <span className="block text-[#F26B2B]">real hiring managers score you.</span>
            </h1>
            <p className="mt-2 text-sm text-gray-500">
              Choose your intensity — from supportive Friend to brutal Drill Sergeant. BARS-scored feedback after every session.
            </p>

            <div className="mt-5 rounded-2xl border-2 border-gray-200 bg-white px-3 py-1.5 focus-within:border-[#F26B2B]">
              <div className="flex items-center gap-2">
                <svg viewBox="0 0 24 24" className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-3.5-3.5" strokeLinecap="round" />
                </svg>
                <input
                  type="text"
                  placeholder="Role, skills, or company..."
                  className="min-w-0 flex-1 bg-transparent py-2.5 text-sm outline-none placeholder:text-gray-400"
                />
                <button
                  type="button"
                  onClick={handleMainSearchClick}
                  className="rounded-xl bg-[#F26B2B] px-4 py-2.5 text-sm font-semibold text-white"
                >
                  Search
                </button>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              <span className="text-xs text-gray-400">Popular:</span>
              <button type="button" className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-600">Product Analyst</button>
              <button type="button" className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-600">Data Engineer</button>
              <button type="button" className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-600">BI Developer</button>
            </div>
          </div>
          <div className="flex gap-2.5 bg-white px-5 py-4">
            <div className="flex-1 rounded-2xl border border-gray-200 py-3 text-center">
              <p className="text-xl font-semibold text-[#F26B2B]">10K+</p>
              <p className="text-[11px] text-gray-500">Jobs</p>
            </div>
            <div className="flex-1 rounded-2xl border border-gray-200 py-3 text-center">
              <p className="text-xl font-semibold text-[#F26B2B]">50K+</p>
              <p className="text-[11px] text-gray-500">Interviews</p>
            </div>
            <div className="flex-1 rounded-2xl border border-gray-200 py-3 text-center">
              <p className="text-xl font-semibold text-[#F26B2B]">85%</p>
              <p className="text-[11px] text-gray-500">Success</p>
            </div>
          </div>

          <div className="px-5 py-7">
            <h2 className="text-center text-2xl font-semibold">How it works</h2>
            <div className="mt-4 grid grid-cols-2 gap-2.5">
              {mobileHowItems.map((item) => (
                <div key={item.title} className="rounded-2xl border border-gray-200 bg-white p-4 text-center">
                  <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-orange-50 text-[#F26B2B]">
                    {item.icon}
                  </div>
                  <h3 className="mt-2.5 text-sm font-semibold">{item.title}</h3>
                  <p className="mt-1 text-[11px] text-gray-500">{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mx-5 mb-6 rounded-[22px] bg-[#1A2332] px-6 py-8 text-center text-white relative overflow-hidden">
            <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-[#F26B2B]/20" />
            <div className="absolute -left-8 -bottom-8 h-20 w-20 rounded-full bg-[#F26B2B]/15" />
            <h2 className="relative text-2xl font-semibold">Ready to start?</h2>
            <p className="relative mt-2 text-sm text-slate-300">
              Join thousands of specialists who have already found their dream job
            </p>
            <button
              type="button"
              onClick={handleStartSearchingClick}
              className="relative mt-5 inline-flex items-center gap-2 rounded-2xl bg-[#F26B2B] px-6 py-3 text-sm font-semibold text-white"
            >
              Start searching for free
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          <section className="border-t border-gray-200 bg-white px-5 py-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-base font-semibold">
                  Job<span className="text-[#F26B2B]">Skills</span>
                </p>
                <p className="mt-1 max-w-[180px] text-[11px] text-gray-500">
                  Smart job search and interview prep with an AI agent.
                </p>
              </div>
              <div className="flex gap-6 text-xs">
                <div>
                  <p className="font-semibold text-gray-800">Product</p>
                  <button type="button" onClick={() => navigate('/search')} className="mt-1 block text-gray-500">Find a Job</button>
                  <button type="button" onClick={() => navigate('/pricing')} className="mt-1 block text-gray-500">Pricing</button>
                  <button type="button" onClick={() => navigate('/blog')} className="mt-1 block text-gray-500">Blog</button>
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Legal</p>
                  <button type="button" onClick={() => navigate('/terms')} className="mt-1 block text-gray-500">Terms</button>
                  <button type="button" onClick={() => navigate('/refund-policy')} className="mt-1 block text-gray-500">Refund</button>
                  <button type="button" onClick={() => navigate('/contacts')} className="mt-1 block text-gray-500">Contacts</button>
                </div>
              </div>
            </div>
            <div className="mt-4 border-t border-gray-200 pt-3 text-center">
              <p className="text-[10px] text-gray-400">© 2026 Yevhenii Lisechko. All rights reserved.</p>
            </div>
          </section>
        </section>

        <section className="hidden md:block">
          <section className="max-w-6xl mx-auto px-6 py-16">
            <div className="flex flex-col items-center text-center gap-6">
              <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-sm text-sm text-gray-500">
                <span className="w-2 h-2 bg-emerald-500 rounded-full" />
                Over 10,000 IT jobs
              </div>

              <div className="space-y-3">
                <h1 className="text-4xl md:text-5xl font-semibold leading-tight">
                  The AI interview coach that trains you like
                  <span className="block text-[#FF7A45]">real hiring managers score you.</span>
                </h1>
                <p className="text-gray-500 max-w-2xl mx-auto">
                  Choose your intensity — from supportive Friend to brutal Drill Sergeant. BARS-scored feedback after every session.
                </p>
              </div>

              <div className="w-full max-w-2xl bg-white rounded-3xl shadow-md p-2 flex items-center gap-3">
                <svg viewBox="0 0 24 24" className="ml-3 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-3.5-3.5" strokeLinecap="round" />
                </svg>
                <input
                  type="text"
                  placeholder="Role, skills, or company..."
                  className="flex-1 w-full py-3 outline-none text-sm text-gray-700 placeholder-gray-400 bg-transparent"
                />
                <button
                  type="button"
                  onClick={handleMainSearchClick}
                  className="btn-orange px-6 py-3 text-sm rounded-2xl"
                >
                  Search
                </button>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-gray-500">
                <span>Popular:</span>
                <button type="button" className="px-4 py-2 bg-white rounded-full shadow-sm text-gray-600">Product Analyst</button>
                <button type="button" className="px-4 py-2 bg-white rounded-full shadow-sm text-gray-600">Data Engineer</button>
                <button type="button" className="px-4 py-2 bg-white rounded-full shadow-sm text-gray-600">BI Developer</button>
              </div>

              <div className="mt-6 grid grid-cols-3 gap-6 w-full max-w-3xl">
                <div className="bg-white rounded-3xl p-6 shadow-sm text-center">
                  <div className="text-2xl font-semibold text-[#FF7A45]">10K+</div>
                  <div className="text-sm text-gray-500 mt-1">Jobs</div>
                </div>
                <div className="bg-white rounded-3xl p-6 shadow-sm text-center">
                  <div className="text-2xl font-semibold text-[#FF7A45]">50K+</div>
                  <div className="text-sm text-gray-500 mt-1">Interviews</div>
                </div>
                <div className="bg-white rounded-3xl p-6 shadow-sm text-center">
                  <div className="text-2xl font-semibold text-[#FF7A45]">85%</div>
                  <div className="text-sm text-gray-500 mt-1">Successful offers</div>
                </div>
              </div>
            </div>
          </section>

          <section className="bg-white/60 py-16">
            <div className="max-w-6xl mx-auto px-6">
              <h2 className="text-3xl font-semibold text-center">How it works</h2>
              <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-6">
                {mobileHowItems.map((item) => (
                  <div key={`desktop-${item.title}`} className="bg-white rounded-3xl p-6 shadow-sm text-center">
                    <div className="w-14 h-14 rounded-2xl bg-orange-50 flex items-center justify-center mx-auto text-[#F26B2B]">
                      {item.icon}
                    </div>
                    <h3 className="mt-4 font-semibold text-gray-800">{item.title}</h3>
                    <p className="text-sm text-gray-500 mt-2">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-16">
            <div className="max-w-6xl mx-auto px-6">
              <div className="bg-[#1E293B] rounded-[32px] px-6 py-12 md:px-14 md:py-14 text-center text-white shadow-lg">
                <h2 className="text-3xl font-semibold">Ready to start?</h2>
                <p className="mt-3 text-sm text-blue-100">
                  Join thousands of specialists who have already found their dream job
                </p>
                <button
                  type="button"
                  onClick={handleStartSearchingClick}
                  className="btn-orange mt-8 px-8 py-3 rounded-2xl text-sm"
                >
                  Start searching for free
                </button>
              </div>
            </div>
          </section>
        </section>
      </main>

      <div className="hidden md:block">
        <Footer />
      </div>
    </div>
  );
}
